import React, { useEffect, useRef } from "react";
import Header from "@/pages/components/header";

import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";

import { Outlet, useLocation, useNavigate } from "react-router-dom";
import LeftSidebar from "@/pages/home/left-sidebar";
import { LocationProvider } from "@/context/location-provider";
import AppAlertDialog from "@/pages/components/app-alert-dialog";
import { useUserDataQueries } from "@/hooks/use-login";

import { useGroupProvider } from "@/context/group-provider";
import useAuth from "@/context/auth-provider";
import { useIsRestoring } from "@tanstack/react-query";
import {
  useDataAccessConfigQuery,
  useDataAccessListQuery,
} from "@/hooks/use-data-access";
import { getQueue } from "@/lib/data-access-utils";
import LoadingIndiator from "@/pages/components/loading-indiator";
import ScreenRouteNames from "@/constants/screen-route-names";
import RightSidebar from "@/pages/components/right-sidebar";
import { useCourtOrderDependentQuery } from "@/hooks/use-court-order-dependents";
import { updateCacheWithCourtOrderDependents } from "@/lib/court-order-utils";
import { HeaderSkeleton } from "@/pages/components/header-skeleton";
import {
  mobileViewSignalUrl,
  closeMobileView,
} from "@/signals/mobile-view-signal";
import { useSignals } from "@preact/signals-react/runtime";
import ChatbotPopup from "@/pages/components/chatbot-popup/chatbot-popup";
import { queryClient } from "@/hooks/use-query-client";
import { alwaysAccessiblePaths, QueryKeys } from "@/constants";
import type { GlobalFlag } from "@/model/global-flag";
import type { MobileViewRef } from "@/pages/mobile-view";
import ScrollToTop from "@/pages/components/scroll-to-top/scroll-to-top";
import { useUiLabels } from "@/hooks/use-ui-labels";
import { setAppAlertDialogs } from "@/store/app-alert-dialog/app-alert-dialog-slice";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import {
  globalFlagRoutes,
  getLegacyNameForPath,
} from "@/constants/screen-route-names";
import { ErrorNotificationType } from "@/pages/components/error-notification";
import { useNotificationError } from "@/context/error-provider";
import PaperlessEobAlert from "@/pages/components/paperlessEob/GoPaperlessEobAlert";
import { usePaperlessEob } from "@/hooks/use-paperlessEob";
import MobileView from "@/pages/mobile-view";

const GuestLayout: React.FC = () => {
  const mobileViewRef = useRef<MobileViewRef>(null);

  useSignals(); // Enable signals reactivity in this component

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { groupData } = useGroupProvider();
  const { authData } = useAuth();
  const isRestoring = useIsRestoring();
  //console.log("From GuestLayout location:->", location.pathname);

  //console.log("## signal", mobileViewSignalUrl.value);

  //Check if the user is trying to access the restricted page
  // if (
  //   authData?.user?.route !== "home" &&
  //   location.pathname !== `/${authData?.user?.route}`
  // ) {
  //   return <Navigate to={`/${ScreenRouteNames.ACCESS_DENIED.path}`} replace />;
  // }
  //console.log("Guest Test point :1");

  const { demographicsQuery, coverageYearsQuery } = useUserDataQueries({
    groupId: groupData?.group_id || "",
    divisionId: groupData?.division_id || "",
    accessToken: authData?.user?.access_token || "",
    username: authData?.user?.username || "",
  });
  const globalFlag = queryClient.getQueryData([
    QueryKeys.GLOBAL_FLAGS,
    authData?.user?.username,
  ]);

  const uiLabelsQuery = useUiLabels();

  const { paperlessEobStatus, isLoadingStatus: isPaperlessEobLoading } =
    usePaperlessEob(demographicsQuery?.data?.memberId);

  // Route guard — mirrors MainLayout logic
  const featureListStatus = useSelector(
    (state: RootState) => state.featureList?.status,
  );
  const byPlan = useSelector(
    (state: RootState) => state.featureList?.byPlan ?? {},
  );
  const { claimCoverageCode, claimCoverageCodeDesc } = useSelector(
    (state: RootState) => state.planCoverageInfo?.activeCoverage ?? null,
  );
  const { addError } = useNotificationError();

  const currentRoute = location.pathname.split("/")[1];

  const globalFlagData = (
    queryClient.getQueryData([
      QueryKeys.GLOBAL_FLAGS,
      authData?.user?.username,
    ]) as { data: GlobalFlag[] } | undefined
  )?.data?.[0];

  const isGuardReady = featureListStatus === "succeeded" && !!claimCoverageCode;
  const isAlwaysAccessible = alwaysAccessiblePaths?.includes(location.pathname);

  const inFeatureList = isGuardReady
    ? (byPlan[claimCoverageCode!] ?? []).some(
        (f) => f.action?.web_route === currentRoute,
      )
    : true;

  const isManagedByAnyPlan = Object.values(byPlan).some((features) =>
    features.some((f) => f.action?.web_route === currentRoute),
  );

  const inGlobalFlag =
    globalFlagData && !isManagedByAnyPlan
      ? (
          Object.entries(globalFlagRoutes) as [keyof GlobalFlag, string[]][]
        ).some(
          ([flagKey, paths]) =>
            globalFlagData[flagKey] && paths.includes(currentRoute),
        )
      : false;

  const shouldBlock =
    isGuardReady && !inFeatureList && !inGlobalFlag && !isAlwaysAccessible;

  const lastBlockedRoute = useRef<string | null>(null);

  useEffect(() => {
    if (!shouldBlock) {
      lastBlockedRoute.current = null;
      return;
    }

    const route = location.pathname.split("/")[1];
    if (lastBlockedRoute.current === route) return;
    lastBlockedRoute.current = route;

    const featureName =
      getLegacyNameForPath(route) ??
      route.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

    addError({
      message: `"${featureName}" is not available in your ${claimCoverageCodeDesc} plan.`,
      type: ErrorNotificationType.ERROR,
    });

    navigate(`/${ScreenRouteNames.HOME.path}`, { replace: true });
  }, [shouldBlock]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    isLoading: isCourtOrderDependentLoading,
    data: courtOrderDependent,
    isFetched: isCourtOrderDependentFetched,
    isSuccess: isCourtOrderDependentSuccess,
  } = useCourtOrderDependentQuery();

  const { data: dataAccessConfig, isFetched: isDataAccessConfigFetched } =
    useDataAccessConfigQuery();

  const { data: dataAccessList, isFetched: isDataAccessListFetched } =
    useDataAccessListQuery(dataAccessConfig!);

  if (
    isCourtOrderDependentFetched &&
    isCourtOrderDependentSuccess &&
    courtOrderDependent !== null &&
    demographicsQuery?.data
  ) {
    updateCacheWithCourtOrderDependents(
      courtOrderDependent,
      demographicsQuery?.data?.memberId,
    );
  }

  if (
    isDataAccessConfigFetched &&
    isDataAccessListFetched &&
    dataAccessList &&
    demographicsQuery?.data?.memberId
  ) {
    const queue = getQueue(dataAccessList, demographicsQuery?.data?.memberId);
    dispatch(setAppAlertDialogs(queue));
  }

  if (
    demographicsQuery.isLoading ||
    isPaperlessEobLoading ||
    isRestoring ||
    uiLabelsQuery.isLoading ||
    coverageYearsQuery.isLoading
  ) {
    return <LoadingIndiator />;
  }
  if (demographicsQuery.isError) {
    return <div>Error</div>;
  }

  return (
    <SidebarProvider>
      {paperlessEobStatus?.data?.showDisclaimer && <PaperlessEobAlert />}
      <AppAlertDialog />
      <LocationProvider>
        <div className="flex min-h-screen w-full flex-col">
          <ScrollToTop />
          {isCourtOrderDependentLoading ? <HeaderSkeleton /> : <Header />}
          <div className="hide-from-third-app full-width flex-1 overflow-hidden md:grid md:grid-cols-[auto,1fr]">
            <div className="hide-from-third-app">
              <LeftSidebar />
            </div>
            <SidebarInset>{shouldBlock ? null : <Outlet />}</SidebarInset>
          </div>
        </div>
        {(globalFlag as { data: GlobalFlag[] })?.data?.[0]?.showAIChat &&
        !isRestoring ? (
          <ChatbotPopup />
        ) : null}
      </LocationProvider>
      <RightSidebar
        title="Mobile App View"
        isOpen={Boolean(mobileViewSignalUrl.value)}
        onClose={() => {
          // console.log("Debug: Parent - Closing mobile view...");
          mobileViewRef.current?.resetIframeSession();

          window.setTimeout(() => {
            if (mobileViewSignalUrl.value) {
              closeMobileView();
            }
          }, 1000);
        }}
      >
        {mobileViewSignalUrl.value && (
          <MobileView url={mobileViewSignalUrl.value} ref={mobileViewRef} />
        )}
      </RightSidebar>
    </SidebarProvider>
  );
};

export default GuestLayout;
